import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { Loader } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useCreateForgotPassword } from '@/gen/publicApi'

import { Button } from '@/components/button'
import { Form } from '@/components/form'
import FormInput from '@/components/form/formInput.tsx'
import { useToast } from '@/components/toast/useToast.ts'

import { mapProblemDetailErrors } from '@/utils/validation.ts'

const forgotPasswordFormSchema = z.object({
    email: z.string().email(),
})

type ForgotPasswordFormSchema = z.infer<typeof forgotPasswordFormSchema>

export default function ForgotPasswordForm() {
    const { toast } = useToast()
    const navigate = useNavigate()
    const forgotPasswordMutator = useCreateForgotPassword()

    const form = useForm<ForgotPasswordFormSchema>({
        resolver: zodResolver(forgotPasswordFormSchema),
        mode: 'onBlur',
    })

    const onSubmit = (values: ForgotPasswordFormSchema) => {
        forgotPasswordMutator.mutate(values, {
            onSuccess: () => {
                toast({
                    variant: 'success',
                    title: 'Password reset email sent!',
                    description: `An email has been sent to ${values.email} with instructions on how to reset your password.`,
                })
                navigate({
                    to: '/',
                    search: {
                        welcome: true,
                    },
                })
            },
            // @ts-expect-error - Kubb generated code is not typed correctly
            onError: (error: AxiosError) => {
                mapProblemDetailErrors(error, form.formState.errors)
                toast({
                    variant: 'destructive',
                    title: 'Forgot password failed!',
                    description: `Failed to send forgot password, please try again`,
                })
            },
        })
    }

    return (
        <>
            <p className="mb-2 block text-center">
                If you have forgotten your password, enter your email address below and we will send you an email with
                reset instructions.
            </p>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
                    <FormInput
                        control={form.control}
                        name="email"
                        label="Email"
                        className="text-left"
                        autoComplete="email"
                    />

                    <div className="flex items-center justify-between">
                        <Button type="submit" disabled={forgotPasswordMutator.isPending}>
                            {forgotPasswordMutator.isPending && <Loader className="mr-2 h-4 w-4 animate-spin" />}
                            Send Reset Email
                        </Button>
                        <Button variant="ghost">
                            <Link to={'/'}>Return to login</Link>
                        </Button>
                    </div>
                </form>
            </Form>
        </>
    )
}
